import type {
  SessionExportParseWorkerResponse,
} from './sessionExportParse.worker';

/** Parse large session exports off the main thread. */
export const SESSION_JSON_WORKER_MIN_BYTES = 512 * 1024;

let parseWorker: Worker | null = null;
let nextRequestId = 1;
const pending = new Map<number, {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
}>();

function terminateWorker() {
  parseWorker?.terminate();
  parseWorker = null;
}

function rejectAllPending(message: string) {
  for (const [, entry] of pending) {
    entry.reject(new Error(message));
  }
  pending.clear();
}

function ensureWorker() {
  if (parseWorker || typeof Worker === 'undefined') {
    return parseWorker;
  }

  parseWorker = new Worker(
    new URL('./sessionExportParse.worker.ts', import.meta.url),
    { type: 'module' },
  );

  parseWorker.onmessage = (event: MessageEvent<SessionExportParseWorkerResponse>) => {
    const entry = pending.get(event.data.id);
    if (!entry) {
      return;
    }

    pending.delete(event.data.id);
    if (event.data.ok) {
      entry.resolve(event.data.data);
      return;
    }

    entry.reject(new Error(event.data.error));
  };

  parseWorker.onerror = () => {
    rejectAllPending('Session JSON worker failed');
    terminateWorker();
  };

  return parseWorker;
}

export async function parseSessionExportJson(text: string): Promise<unknown> {
  if (text.length < SESSION_JSON_WORKER_MIN_BYTES || typeof Worker === 'undefined') {
    return JSON.parse(text);
  }

  const worker = ensureWorker();
  if (!worker) {
    return JSON.parse(text);
  }

  const id = nextRequestId++;

  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker.postMessage({ id, text });
  });
}
